import { useEffect, useMemo, useState } from "react";
import { api, fmt, imgUrl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Search, Check, ArrowRight } from "lucide-react";

const SECTION_ORDER = ["youtube", "press", "instagram", "viral"];

const PRESS_SECTIONS = [
  { key: "music_entertainment", title: "Music + Entertainment" },
  { key: "top_tier", title: "Top Tier Media" },
  { key: "seo", title: "Press + Google Ranking" },
];

function ServiceCard({ s }) {
  const { items, add } = useCart();
  const inCart = items.some((i) => i.id === s.id);
  const isDeposit = s.checkout_type === "deposit_approval";
  const price = s.sale_price > 0 ? s.sale_price : s.price;
  return (
    <article data-testid={`service-card-${s.id}`}
      className="border border-white/10 hover:border-brand/60 transition-colors duration-300 p-6 flex flex-col hover:-translate-y-1 bg-[#0A0A0A]">
      <h3 className="font-display text-2xl text-white">{s.name}</h3>
      {s.description && <p className="text-sm text-zinc-400 mt-2 leading-relaxed flex-1">{s.description}</p>}
      <div className="flex items-end justify-between mt-6 gap-3">
        <div>
          <p data-testid={`service-price-${s.id}`} className="font-display text-3xl text-white">
            {s.sale_price > 0 && <span className="text-lg text-zinc-500 line-through mr-2">{fmt(s.price)}</span>}
            {fmt(price)}<span className="text-sm text-zinc-500">{s.unit === "monthly" ? "/mo" : ""}</span>
          </p>
          {isDeposit && <p className="text-xs text-zinc-500 mt-1">{fmt(s.deposit_amount)} deposit today · balance after approval</p>}
        </div>
        <button data-testid={`add-to-cart-${s.id}`} onClick={() => add(s)} disabled={inCart}
          className={`flex items-center gap-2 text-xs font-semibold tracking-widest uppercase px-5 py-3 transition-colors whitespace-nowrap ${
            inCart ? "bg-white/10 text-zinc-400 cursor-default" : "bg-brand hover:bg-brand-hover text-white"
          }`}>
          {inCart ? <><Check className="w-3.5 h-3.5" /> In Cart</> : <>Start Campaign <ArrowRight className="w-3.5 h-3.5" /></>}
        </button>
      </div>
    </article>
  );
}

export default function MarketingPage() {
  const [services, setServices] = useState(null);
  const [q, setQ] = useState("");
  const [sub, setSub] = useState("Custom marketing campaigns tailored for your brand");

  useEffect(() => {
    api.get("/marketing-services").then((r) => setServices(r.data)).catch(() => setServices([]));
    api.get("/site/content").then((r) => {
      if (r.data?.site_text?.marketing_sub) setSub(r.data.site_text.marketing_sub);
    }).catch(() => {});
  }, []);

  const searching = q.trim().length > 0;
  const filtered = useMemo(() => {
    if (!services) return [];
    const needle = q.trim().toLowerCase();
    return services.filter((s) => !needle || s.name.toLowerCase().includes(needle) || (s.description || "").toLowerCase().includes(needle));
  }, [services, q]);

  const byPlatform = (p) => (services || []).filter((s) => s.platform === p);
  const pressBy = (section) => byPlatform("press").filter((s) => (s.section || "music_entertainment") === section);
  const levelsOf = (list) => {
    const groups = {};
    list.forEach((s) => { const lv = s.level || "1"; (groups[lv] = groups[lv] || []).push(s); });
    return Object.entries(groups).sort(([a], [b]) => Number(a) - Number(b));
  };

  return (
    <div data-testid="marketing-page">
      <section className="relative grain overflow-hidden">
        <img src={imgUrl("/api/files/bookedbybenj/site/artist.png")} alt="Artist performing live"
          className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/70" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-28">
          <p className="text-xs tracking-[0.4em] uppercase text-brand mb-4">Music Marketing</p>
          <h1 className="font-display text-6xl sm:text-7xl text-white mb-4">Marketing Services</h1>
          <p className="text-base md:text-xl text-zinc-200 max-w-3xl leading-relaxed">{sub}</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="relative max-w-xl mb-16">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <input data-testid="service-search-input" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search services…"
            className="w-full bg-white/5 border border-white/10 focus:border-brand outline-none text-sm text-white pl-11 pr-4 py-3.5 transition-colors" />
        </div>

        {services === null ? <p className="text-zinc-500">Loading…</p> : searching ? (
          filtered.length === 0 ? (
            <p data-testid="no-services-msg" className="text-zinc-500">No services match your search.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((s) => <ServiceCard key={s.id} s={s} />)}
            </div>
          )
        ) : (
          <>
            {/* YOUTUBE */}
            <section data-testid="section-youtube" className="mb-20">
              <div className="flex items-center gap-4 mb-3">
                <img src="https://cdn.simpleicons.org/youtube/FF0000" alt="YouTube" className="h-10 w-10" />
                <h2 className="font-display text-5xl text-white">YouTube</h2>
              </div>
              <p className="text-xs tracking-[0.25em] uppercase text-zinc-400 mb-8">
                All campaigns generate Views · Likes · Comments · Shares · Subscribers
              </p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {byPlatform("youtube").map((s) => <ServiceCard key={s.id} s={s} />)}
              </div>
            </section>

            {/* PRESS */}
            <section data-testid="section-press" className="mb-20">
              <h2 className="font-display text-5xl text-white mb-3">Press</h2>
              <p className="text-xs tracking-[0.25em] uppercase text-brand mb-10">
                Each placement includes a backlink for SEO & Google ranking
              </p>
              {PRESS_SECTIONS.map((sec) => {
                const items = pressBy(sec.key);
                if (!items.length) return null;
                return (
                  <div key={sec.key} className="mb-12">
                    <h3 className="text-sm tracking-[0.3em] uppercase text-white border-b border-white/10 pb-3 mb-6">{sec.title}</h3>
                    {sec.key === "seo" ? (
                      <div className="max-w-xl">{items.map((s) => <ServiceCard key={s.id} s={s} />)}</div>
                    ) : (
                      levelsOf(items).map(([lv, lvItems]) => (
                        <div key={lv} className="mb-8">
                          <p className="text-xs tracking-widest uppercase text-zinc-500 mb-4">
                            Level {lv} — <span className="text-brand">{fmt(lvItems[0].sale_price > 0 ? lvItems[0].sale_price : lvItems[0].price)}</span> each
                          </p>
                          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                            {lvItems.map((s) => <OutletChip key={s.id} s={s} />)}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                );
              })}
            </section>

            {/* INSTAGRAM */}
            <section data-testid="section-instagram" className="mb-20">
              <div className="flex items-center gap-4 mb-8">
                <img src="https://cdn.simpleicons.org/instagram/E4405F" alt="Instagram" className="h-10 w-10" />
                <h2 className="font-display text-5xl text-white">Instagram</h2>
              </div>
              {byPlatform("instagram").length === 0 ? (
                <p className="text-sm text-zinc-500">Instagram services coming soon.</p>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {byPlatform("instagram").map((s) => <ServiceCard key={s.id} s={s} />)}
                </div>
              )}
            </section>

            {/* VIRAL */}
            {byPlatform("viral").length > 0 && (
              <section data-testid="section-viral" className="mb-20">
                <h2 className="font-display text-5xl text-white mb-8">Viral</h2>
                <div className="max-w-xl">
                  {byPlatform("viral").map((s) => <ServiceCard key={s.id} s={s} />)}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function OutletChip({ s }) {
  const { items, add } = useCart();
  const inCart = items.some((i) => i.id === s.id);
  return (
    <div data-testid={`service-card-${s.id}`}
      className="border border-white/10 hover:border-brand/60 bg-[#0A0A0A] p-4 flex flex-col justify-between transition-colors group">
      <p className="text-sm text-white font-medium leading-snug">{s.name}</p>
      <button data-testid={`add-to-cart-${s.id}`} onClick={() => add(s)} disabled={inCart}
        className={`mt-4 text-[10px] font-semibold tracking-widest uppercase px-3 py-2 transition-colors self-start ${
          inCart ? "bg-white/10 text-zinc-500 cursor-default" : "bg-brand hover:bg-brand-hover text-white"
        }`}>
        {inCart ? "In Cart" : "Start Campaign"}
      </button>
    </div>
  );
}
